import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { formatAmount, formatEventDate } from "@/lib/format";
import { ArrowLeft, Calendar } from "lucide-react";
import PerforatedDivider from "@/components/PerforatedDivider";

export default function VoucherDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const qs = token ? `?token=${encodeURIComponent(token)}` : "";
    api.get(`/vouchers/${id}${qs}`)
      .then((r) => setV(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Bon introuvable"))
      .finally(() => setLoading(false));
  }, [id, token]);

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (err) return <div className="z-container">{err}</div>;

  return (
    <div className="z-container" style={{ maxWidth: 480 }} data-testid="voucher-detail">
      <div style={{ background: "white", borderRadius: 20, padding: 22, border: "1px solid var(--z-border)", boxShadow: "0 4px 18px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span className="z-badge z-badge-teal">{v.type === "amount" ? "💰 Montant" : "🎁 Articles"}</span>
          {v.status === "active" && <span className="z-badge z-badge-success">Actif</span>}
          {v.status === "fully_used" && <span className="z-badge">Épuisé</span>}
          {v.status === "expired" && <span className="z-badge" style={{ background: "rgba(161,44,123,0.12)", color: "var(--z-error)" }}>Expiré</span>}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 6px", color: "var(--z-teal)" }} data-testid="vd-title">{v.title}</h2>
        <p style={{ fontSize: 13, color: "var(--z-muted)", margin: "0 0 14px" }}>par {v.merchant_name}</p>

        {v.type === "amount" ? (
          <div style={{ background: "var(--z-bg)", padding: 18, borderRadius: 12, textAlign: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: "var(--z-muted)" }}>Solde disponible</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: "var(--z-teal)" }} data-testid="vd-balance">
              {formatAmount(v.remaining_amount, v.currency)}
            </div>
            <div style={{ fontSize: 12, color: "var(--z-muted)" }}>sur {formatAmount(v.initial_amount, v.currency)} initial</div>
          </div>
        ) : (
          <div style={{ background: "var(--z-bg)", padding: 14, borderRadius: 12, marginBottom: 14 }}>
            {v.items.map((it, i) => {
              const used = it.used_quantity >= it.quantity;
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < v.items.length - 1 ? "1px solid var(--z-border)" : "none", color: used ? "var(--z-muted)" : "var(--z-text)", textDecoration: used ? "line-through" : "none" }} data-testid={`item-row-${it.id}`}>
                  <span>{it.label}</span>
                  <strong>{it.quantity - it.used_quantity}/{it.quantity}</strong>
                </div>
              );
            })}
          </div>
        )}

        {v.expires_at && (
          <p style={{ fontSize: 13, color: "var(--z-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            <Calendar size={14} /> Expire le {formatEventDate(v.expires_at)}
          </p>
        )}

        <PerforatedDivider />

        <div style={{ textAlign: "center", marginTop: 14 }}>
          <p style={{ fontSize: 13, color: "var(--z-muted)", margin: "0 0 10px" }}>Présentez ce QR au marchand :</p>
          {v.qr_payload && (
            <div style={{ display: "inline-block", padding: 14, background: "white", borderRadius: 12 }}>
              <QRCodeSVG value={v.qr_payload} size={200} level="H" data-testid="vd-qr" />
            </div>
          )}
          <div style={{ marginTop: 12, fontFamily: "monospace", fontSize: 16, fontWeight: 700, color: "var(--z-teal)" }} data-testid="vd-code">
            {v.code}
          </div>
        </div>

        {v.redemptions?.length > 0 && (
          <>
            <div className="z-divider" />
            <h4 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px" }}>Historique</h4>
            {v.redemptions.map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: "var(--z-muted)", padding: "4px 0" }}>
                {formatEventDate(r.at)} — {r.amount ? formatAmount(r.amount, v.currency) : `${r.item_ids?.length || 0} article(s)`} par {r.by_merchant_name || "Marchand"}
              </div>
            ))}
          </>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 18 }}>
        <Link to="/vouchers" className="z-link" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <ArrowLeft size={14} /> Retour au portefeuille
        </Link>
      </div>
    </div>
  );
}
